import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarPlus, FaClock, FaTrash, FaCheck, FaTimes, FaUser } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Horarios disponibles para seleccionar (8:00 AM - 8:00 PM) con intervalos de 30 minutos
const TIME_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'
];

const AdminGoldCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [availability, setAvailability] = useState({});
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('availability'); // 'availability' | 'bookings'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [availRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/gold-calendar/admin/availability`),
        axios.get(`${API}/gold-calendar/admin/bookings`)
      ]);
      setAvailability(availRes.data.by_date || {});
      setBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSelect = (time) => {
    if (selectedTimes.includes(time)) {
      setSelectedTimes(selectedTimes.filter(t => t !== time));
    } else {
      setSelectedTimes([...selectedTimes, time].sort());
    }
  };

  const handleCreateAvailability = async () => {
    if (selectedTimes.length === 0) {
      toast.error('Selecciona al menos un horario');
      return;
    }

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const slots = selectedTimes.map(time => {
      const [hours, mins] = time.split(':').map(Number);
      let endHours = hours;
      let endMins = mins + 30;
      if (endMins >= 60) {
        endHours += 1;
        endMins = 0;
      }
      return {
        date: dateStr,
        start_time: time,
        end_time: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
      };
    });

    try {
      await axios.post(`${API}/gold-calendar/admin/availability`, { slots });
      toast.success(`Se crearon ${slots.length} horarios disponibles`);
      setSelectedTimes([]);
      fetchData();
    } catch (error) {
      toast.error('Error al crear disponibilidad');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/gold-calendar/admin/availability/${slotId}`);
      toast.success('Horario eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/gold-calendar/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Reserva ${status === 'confirmed' ? 'confirmada' : status === 'cancelled' ? 'cancelada' : 'actualizada'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar reserva');
    }
  };

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const slotsForSelectedDate = availability[selectedDateStr] || [];

  // Fechas que tienen slots disponibles
  const datesWithSlots = Object.keys(availability);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Calendario Gold Plan
          </h2>
          <p className="text-gray-400">Gestiona tu disponibilidad para sesiones personalizadas</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3">
        <Button
          onClick={() => setActiveTab('availability')}
          className={`rounded-full px-6 font-bold uppercase tracking-wider ${
            activeTab === 'availability' 
              ? 'text-white' 
              : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
          }`}
          style={activeTab === 'availability' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
        >
          <FaCalendarPlus className="mr-2" /> Disponibilidad
        </Button>
        <Button
          onClick={() => setActiveTab('bookings')}
          className={`rounded-full px-6 font-bold uppercase tracking-wider ${
            activeTab === 'bookings' 
              ? 'text-white' 
              : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
          }`}
          style={activeTab === 'bookings' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
        >
          <FaUser className="mr-2" /> Reservas ({bookings.filter(b => b.status === 'pending').length})
        </Button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'availability' ? (
          <motion.div
            key="availability"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Panel Izquierdo - Calendario y Selector de Horarios */}
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Agregar Disponibilidad</CardTitle>
                <CardDescription className="text-gray-400">
                  Selecciona una fecha y los horarios disponibles
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Calendario */}
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={es}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-xl border border-white/10 bg-[#111] text-white"
                    modifiers={{
                      hasSlots: (date) => datesWithSlots.includes(format(date, 'yyyy-MM-dd'))
                    }}
                    modifiersStyles={{
                      hasSlots: { backgroundColor: 'rgba(var(--brand-primary-glow), 0.3)', borderRadius: '8px' }
                    }}
                  />
                </div>

                {/* Fecha Seleccionada */}
                <div className="text-center">
                  <p className="text-lg font-bold text-white">
                    {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                  </p>
                </div>

                {/* Selector de Horarios */}
                <div>
                  <p className="text-sm text-gray-400 mb-3">Selecciona horarios (30 min c/u):</p>
                  <div className="grid grid-cols-5 gap-2">
                    {TIME_SLOTS.map(time => {
                      const isSelected = selectedTimes.includes(time);
                      const isBooked = slotsForSelectedDate.some(s => s.start_time === time && s.is_booked);
                      const isAvailable = slotsForSelectedDate.some(s => s.start_time === time && !s.is_booked);
                      
                      return (
                        <button
                          key={time}
                          onClick={() => !isBooked && !isAvailable && handleTimeSelect(time)}
                          disabled={isBooked}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            isBooked
                              ? 'bg-red-500/30 text-red-300 cursor-not-allowed'
                              : isAvailable
                              ? 'bg-green-500/30 text-green-300 cursor-default'
                              : isSelected
                              ? 'bg-[var(--brand-primary)] text-white'
                              : 'bg-white/10 text-gray-300 hover:bg-white/20'
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-green-500/30"></span> Disponible
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-red-500/30"></span> Reservado
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-[var(--brand-primary)]"></span> Seleccionado
                    </span>
                  </div>
                </div>

                {/* Botón Crear */}
                {selectedTimes.length > 0 && (
                  <Button
                    onClick={handleCreateAvailability}
                    className="w-full rounded-full font-bold text-white py-6"
                    style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                  >
                    <FaCalendarPlus className="mr-2" />
                    Agregar {selectedTimes.length} Horario{selectedTimes.length > 1 ? 's' : ''}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Panel Derecho - Horarios del día seleccionado */}
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">
                  Horarios para {format(selectedDate, "d 'de' MMMM", { locale: es })}
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {slotsForSelectedDate.length} horarios configurados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {slotsForSelectedDate.length === 0 ? (
                  <div className="text-center py-12">
                    <FaClock className="text-5xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
                    <p className="text-gray-400">No hay horarios para este día</p>
                    <p className="text-sm text-gray-500 mt-2">Selecciona horarios a la izquierda</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {slotsForSelectedDate.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(slot => (
                      <div 
                        key={slot.id}
                        className="flex items-center justify-between p-4 rounded-xl transition-all"
                        style={{ 
                          background: slot.is_booked ? 'rgba(var(--brand-primary-glow), 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          border: slot.is_booked ? '1px solid rgba(var(--brand-primary-glow), 0.3)' : '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: slot.is_booked ? 'rgba(var(--brand-primary-glow), 0.3)' : 'rgba(0, 240, 255, 0.3)' }}
                          >
                            <FaClock style={{ color: slot.is_booked ? 'var(--brand-primary)' : '#00F0FF' }} />
                          </div>
                          <div>
                            <p className="font-semibold text-white">
                              {slot.start_time} - {slot.end_time}
                            </p>
                            {slot.is_booked && (
                              <p className="text-sm text-gray-400">
                                Reservado por: {slot.booked_by}
                              </p>
                            )}
                          </div>
                        </div>
                        {!slot.is_booked && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSlot(slot.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <FaTrash />
                          </Button>
                        )}
                        {slot.is_booked && (
                          <Badge style={{ background: 'var(--brand-primary)' }} className="text-white">
                            Reservado
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="bookings"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Reservas de Clientes Gold</CardTitle>
                <CardDescription className="text-gray-400">
                  Gestiona las solicitudes de sesiones personalizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <div className="text-center py-12">
                    <FaUser className="text-5xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
                    <p className="text-gray-400">No hay reservas todavía</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookings.map(booking => (
                      <div 
                        key={booking.id}
                        className="p-4 rounded-xl"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge 
                                className={`${
                                  booking.status === 'pending' ? 'bg-yellow-500' :
                                  booking.status === 'confirmed' ? 'bg-green-500' :
                                  booking.status === 'cancelled' ? 'bg-red-500' :
                                  'bg-blue-500'
                                } text-white`}
                              >
                                {booking.status === 'pending' ? 'Pendiente' :
                                 booking.status === 'confirmed' ? 'Confirmada' :
                                 booking.status === 'cancelled' ? 'Cancelada' :
                                 'Completada'}
                              </Badge>
                              <span className="text-white font-semibold">
                                {format(parseISO(booking.date), "d 'de' MMMM", { locale: es })}
                              </span>
                              <span className="text-gray-400">
                                {booking.start_time} - {booking.end_time}
                              </span>
                            </div>
                            <p className="text-gray-300">
                              <span className="text-gray-500">Cliente:</span> {booking.user_name}
                            </p>
                            <p className="text-gray-400 text-sm">{booking.user_email}</p>
                            {booking.notes && (
                              <p className="text-gray-500 text-sm mt-2 italic">"{booking.notes}"</p>
                            )}
                          </div>
                          
                          {booking.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                                className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                              >
                                <FaCheck className="mr-1" /> Confirmar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                                className="bg-red-500 hover:bg-red-600 text-white rounded-full"
                              >
                                <FaTimes className="mr-1" /> Rechazar
                              </Button>
                            </div>
                          )}
                          
                          {booking.status === 'confirmed' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateBookingStatus(booking.id, 'completed')}
                              className="rounded-full text-white"
                              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                            >
                              Marcar Completada
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminGoldCalendar;
